import {
  ACIDITY_OPTIONS,
  AGING_POTENTIAL_OPTIONS,
  BODY_OPTIONS,
  FINISH_LENGTH_OPTIONS,
  SWEETNESS_OPTIONS,
  TANNINS_OPTIONS,
  WineTypes,
  countries,
} from '@/common/constant';
import type { WineProductItem } from '@/common/domain-model';
import type { WineProduct } from '@/common/generated/wine-product.typebox';
import {
  DynamicImageUploadList,
  DynamicInputList,
  DynamicKeyValueList,
} from '@/components/dynamic-form';
import { useTreatyClient } from '@/lib/client';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  message,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

export const AddWineOrEditWineModal: FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentProduct?: WineProductItem;
}> = ({ open, onClose, onSuccess, currentProduct }) => {
  const [form] = Form.useForm<WineProduct>();
  const client = useTreatyClient();
  const [loading, setLoading] = useState(false);
  const [analyzeText, setAnalyzeText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  const isEditing = Boolean(currentProduct?.id);

  useEffect(() => {
    if (open && currentProduct) {
      const cleanedValues = Object.fromEntries(
        Object.entries({
          ...currentProduct.data,
          id: currentProduct.id,
        }).map(([key, value]) => {
          if (key === 'specifications' && value) {
            // Convert specifications object to array format
            return [
              key,
              Object.entries(value).map(([key, value]) => ({ key, value })),
            ];
          }
          return [key, value === null ? undefined : value];
        }),
      );
      form.setFieldsValue(cleanedValues);
    }
  }, [open, currentProduct, form]);

  const handleClose = () => {
    form.resetFields();
    setAnalyzeText('');
    onClose();
  };

  const handleSubmit = async (values: WineProduct) => {
    try {
      setLoading(true);

      // Clean all array fields
      Object.entries(values).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          values[key] = value.filter((item) => item != null && item !== '');
        }
      });

      // Convert specifications array to object
      if (values.specifications) {
        values.specifications = values.specifications.reduce(
          (acc, { key, value }) => {
            if (key && value) {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );
      }

      if (
        values.serving_temp &&
        !values.serving_temp.min &&
        !values.serving_temp.max
      ) {
        values.serving_temp = null;
      }

      const response = isEditing
        ? await client.api.wine_product({ id: currentProduct!.id }).put({
            ...values,
          })
        : await client.api.wine_product.create.post({
            ...values,
          });

      if (response.data?.success) {
        message.success(
          isEditing ? 'Wine updated successfully' : 'Wine added successfully',
        );
        onSuccess();
        handleClose();
      } else {
        message.error(response.error?.value.message || 'Failed to add wine');
      }
    } catch (error) {
      console.error(
        isEditing ? 'Failed to update wine: ' : 'Failed to add wine: ',
        error,
      );
      message.error(isEditing ? 'Failed to update wine' : 'Failed to add wine');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeText = async () => {
    if (!analyzeText.trim()) {
      message.warning('Please enter text to analyze');
      return;
    }

    try {
      setAnalyzing(true);
      const response = await client.api.wine_product.analyze_text.post({
        text: analyzeText,
      });

      if (response.data?.success) {
        message.success('Wine added successfully');
        onSuccess();
        handleClose();
      } else {
        message.error(response.error?.value.message || 'Failed to add wine');
      }
    } catch (error) {
      console.error('Failed to add wine:', error);
      message.error('Failed to add wine');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Edit Wine' : 'Add Wine'}
      open={open}
      onCancel={handleClose}
      width={920}
      maskClosable={!(analyzing || loading)}
      footer={[
        <Button key="back" onClick={handleClose}>
          Cancel
        </Button>,
        !isEditing ? (
          <Button
            key="analyze"
            type="primary"
            onClick={handleAnalyzeText}
            loading={analyzing}
            disabled={!analyzeText.trim()}
          >
            Add
          </Button>
        ) : (
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        ),
      ]}
    >
      {!isEditing ? (
        <Card className="mb-6">
          <div className="mb-4">
            <h4 className="text-base font-medium mb-2">
              Wine Name (including vintage)
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Enter the full wine name including the vintage year if applicable
            </p>
            <TextArea
              value={analyzeText}
              onChange={(e) => setAnalyzeText(e.target.value)}
              placeholder="Enter wine name..."
              rows={4}
              className="mb-4"
            />
          </div>
        </Card>
      ) : (
        <Form form={form} onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: 'Please enter a title' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label="Price">
                <InputNumber placeholder="Enter price" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="year" label="Year">
                <InputNumber
                  placeholder="Enter year"
                  className="w-full"
                  min={1900}
                  max={new Date().getFullYear()}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country" label="Country">
                <Select placeholder="Select a country">
                  {countries.map((country) => (
                    <Select.Option key={country} value={country}>
                      {country}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="country_other" label="Country (Other)">
                <Input placeholder="Enter country if not in list" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="rating" label="Rating">
                <InputNumber
                  placeholder="Enter rating (0-100)"
                  className="w-full"
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="reviews_count" label="Reviews Count">
                <InputNumber
                  placeholder="Enter reviews count"
                  className="w-full"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="mb-6">
            <Col span={24}>
              <DynamicInputList
                name="product_urls"
                label="Product URLs"
                placeholder="Enter product URL"
                buttonText="Add Product URL"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      try {
                        new URL(value);
                        return Promise.resolve();
                      } catch {
                        return Promise.reject(
                          new Error('Please enter a valid URL'),
                        );
                      }
                    },
                  },
                ]}
              />
            </Col>
          </Row>
          <Row gutter={16} className="mb-6">
            <Col span={24}>
              <DynamicImageUploadList
                name="image_urls"
                label="Images"
                buttonText="Add Image"
                form={form}
              />
            </Col>
          </Row>
          <Divider />
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="wine_type" label="Wine Type">
                <Select placeholder="Select a wine type">
                  {WineTypes.map((wineType) => (
                    <Select.Option key={wineType} value={wineType}>
                      {wineType}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="wine_type_other" label="Wine Type Other">
                <Input placeholder="Enter wine type other" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="description" label="Description">
                <TextArea placeholder="Enter description" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="producer" label="Producer">
                <Input placeholder="Enter producer" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="producer_description"
                label="Producer Description"
              >
                <TextArea placeholder="Enter description" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="alcohol" label="Alcohol">
                <InputNumber
                  placeholder="Enter alcohol"
                  className="w-full"
                  min={0}
                  max={100}
                  step={0.1}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grape" label="Grape">
                <Input placeholder="Enter grape" className="w-full" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="specifications" label="Specifications">
                <DynamicKeyValueList
                  name="specifications"
                  label="Specifications"
                  keyPlaceholder="Enter specification key"
                  valuePlaceholder="Enter specification value"
                  buttonText="Add Specification"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item label="Serving Temperature">
                <Space.Compact>
                  <Form.Item
                    name={['serving_temp', 'min']}
                    noStyle
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const max = getFieldValue(['serving_temp', 'max']);
                          if (value || max) {
                            if (!value) {
                              return Promise.reject(
                                new Error('Please enter minimum temperature'),
                              );
                            }
                            if (max && value > max) {
                              return Promise.reject(
                                new Error(
                                  'Minimum temperature cannot be greater than maximum',
                                ),
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber
                      placeholder="Min °C"
                      className="w-24"
                      min={0}
                    />
                  </Form.Item>
                  <span className="mt-1 px-2 flex items-center">to</span>
                  <Form.Item
                    name={['serving_temp', 'max']}
                    noStyle
                    dependencies={[['serving_temp', 'min']]}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue(['serving_temp', 'min']);
                          if (value || min) {
                            if (!value) {
                              return Promise.reject(
                                new Error('Please enter maximum temperature'),
                              );
                            }
                            if (min && value < min) {
                              return Promise.reject(
                                new Error(
                                  'Maximum temperature cannot be less than minimum',
                                ),
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <InputNumber
                      placeholder="Max °C"
                      className="w-24"
                      min={0}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="body" label="Body">
                <Select placeholder="Select a body">
                  {BODY_OPTIONS.map((body) => (
                    <Select.Option key={body} value={body}>
                      {body}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="acidity" label="Acidity">
                <Select placeholder="Select a acidity">
                  {ACIDITY_OPTIONS.map((acidity) => (
                    <Select.Option key={acidity} value={acidity}>
                      {acidity}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="tannins" label="Tannins">
                <Select placeholder="Select a tannins">
                  {TANNINS_OPTIONS.map((tannins) => (
                    <Select.Option key={tannins} value={tannins}>
                      {tannins}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sweetness" label="Sweetness">
                <Select placeholder="Select a sweetness">
                  {SWEETNESS_OPTIONS.map((sweetness) => (
                    <Select.Option key={sweetness} value={sweetness}>
                      {sweetness}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <DynamicInputList
                name="primary_flavors"
                label="Primary Flavors"
                placeholder="Enter primary flavor"
                buttonText="Add Primary Flavor"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <DynamicInputList
                name="secondary_flavors"
                label="Secondary Flavors"
                placeholder="Enter secondary flavor"
                buttonText="Add Secondary Flavor"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <DynamicInputList
                name="tertiary_flavors"
                label="Tertiary Flavors"
                placeholder="Enter tertiary flavor"
                buttonText="Add Tertiary Flavor"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="finish_length" label="Finish Length">
                <Select placeholder="Select a finish length">
                  {FINISH_LENGTH_OPTIONS.map((finishLength) => (
                    <Select.Option key={finishLength} value={finishLength}>
                      {finishLength}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <DynamicInputList
                name="finish_characteristics"
                label="Finish Characteristics"
                placeholder="Enter finish characteristic"
                buttonText="Add Finish Characteristic"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="aging_potential" label="Aging Potential">
                <Select placeholder="Select a aging potential">
                  {AGING_POTENTIAL_OPTIONS.map((agingPotential) => (
                    <Select.Option key={agingPotential} value={agingPotential}>
                      {agingPotential}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};
